import { forwardRef, type ReactNode } from 'react';
import MuiTypography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { resolvePaletteColor, type ThemePaletteColor } from '../paletteColorPath';

export type TypographyVariant = 'h1' | 'h2' | 'h3' | 'h4' | 'body';

export type TypographyColor = ThemePaletteColor | 'inherit';

export type TypographyProps = {
  children?: ReactNode;
  variant: TypographyVariant;
  /** Theme palette path, e.g. `primary.main`, or `inherit`. */
  color?: TypographyColor;
};

const variantToMui: Record<TypographyVariant, 'h1' | 'h2' | 'h3' | 'h4' | 'body1'> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  body: 'body1'
};

/** Design-system typography; styles live in `Typography.theme.tsx`. */
export const Typography = forwardRef<HTMLElement, TypographyProps>(function Typography(
  { variant, color = 'inherit', children },
  ref
) {
  const theme = useTheme();
  const resolvedColor = color === 'inherit' ? undefined : resolvePaletteColor(theme.palette, color);

  return (
    <MuiTypography
      ref={ref}
      variant={variantToMui[variant]}
      className={['SmTypography-root', `SmTypography-variant-${variant}`].join(' ')}
      sx={resolvedColor ? { color: resolvedColor } : undefined}
    >
      {children}
    </MuiTypography>
  );
});
